import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Form, InputGroup, Table } from 'react-bootstrap';
import { IconReceipt, IconSearch, IconCheck } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useDateFormat } from '../../shared/DateFormatContext';
import { api } from '../../shared/api';
import { usePermissions } from '../../shared/permissions';
import { useRealtimeEvents } from '../../shared/useRealtimeEvents';
import { CenteredLoader } from '../../components/LoadingSpinner';
import EmptyState from '../../components/feedback/EmptyState';
import PageSection from '../../components/layout/PageSection';
import PageShell from '../../components/layout/PageShell';
import StatsCard from '../../components/ui/StatsCard';
import StatusBadge from '../../components/ui/StatusBadge';

const STATUS_COLORS = {
  open: '#206bc4',
  paid: '#2fb344',
  overdue: '#d63939',
};

const normalizeInvoices = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  return payload?.results || [];
};

const Invoices = () => {
  const { t } = useTranslation();
  const { hasPerm } = usePermissions();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const canChangeInvoices = hasPerm('invoices.change_invoice');
  const canViewContracts = hasPerm('contracts.view_contract');

  const { data: invoices = [], isLoading, error } = useQuery({
    queryKey: ['invoices'],
    queryFn: () => api.get('invoices/api/invoices/'),
    select: normalizeInvoices,
  });

  useRealtimeEvents((event) => {
    if (event.model === 'invoices.invoice') {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    }
  });

  const markAsPaidMutation = useMutation({
    mutationFn: (invoiceId) => api.post(`invoices/api/invoices/${invoiceId}/mark_as_paid/`, {}),
    onSuccess: () => {
      toast.success(t('invoices.markPaidSuccess'));
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: () => {
      toast.error(t('invoices.markPaidError'));
    },
  });

  const stats = useMemo(() => {
    const summary = {
      total: invoices.length,
      open: 0,
      paid: 0,
      overdue: 0,
    };

    invoices.forEach((invoice) => {
      if (invoice.status === 'open') summary.open += 1;
      if (invoice.status === 'paid') summary.paid += 1;
      if (invoice.status === 'overdue') summary.overdue += 1;
    });

    return summary;
  }, [invoices]);

  const filteredInvoices = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();

    return invoices.filter((invoice) => {
      const matchesSearch = !needle || [
        invoice.name,
        invoice.contract_name,
        invoice.status_display,
        invoice.changed_by?.username,
      ].some((value) => (value || '').toLowerCase().includes(needle));

      const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [invoices, searchTerm, statusFilter]);

  const { formatDate: fmtDate } = useDateFormat();

  const formatDate = (value) => {
    if (!value) {
      return t('common.notSet');
    }
    return fmtDate(value);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
  };

  if (isLoading) {
    return (
      <PageShell
        pretitle={t('invoices.overview')}
        title={t('invoices.title')}
        description={t('invoices.subtitle')}
      >
        <PageSection>
          <CenteredLoader />
        </PageSection>
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell
        pretitle={t('invoices.overview')}
        title={t('invoices.title')}
        description={t('invoices.subtitle')}
      >
        <PageSection>
          <Alert variant="danger" className="mb-0">{t('invoices.errorFetching')}</Alert>
        </PageSection>
      </PageShell>
    );
  }

  return (
    <PageShell
      pretitle={t('invoices.overview')}
      title={t('invoices.title')}
      description={t('invoices.subtitle')}
      actions={canViewContracts ? (
        <Button as={Link} to="/contracts" variant="outline-secondary">
          <IconReceipt size={16} className="me-2" />
          {t('invoices.openContracts')}
        </Button>
      ) : null}
    >
      <div className="app-stat-grid mb-4">
        <StatsCard title={t('invoices.stats.total')} value={stats.total} icon="receipt" colorClass="primary" />
        <StatsCard title={t('invoices.stats.open')} value={stats.open} icon="clock-hour-4" colorClass="azure" />
        <StatsCard title={t('invoices.stats.paid')} value={stats.paid} icon="circle-check" colorClass="success" />
        <StatsCard title={t('invoices.stats.overdue')} value={stats.overdue} icon="alert-triangle" colorClass="red" />
      </div>

      <PageSection
        title={t('invoices.listTitle')}
        description={t('invoices.sectionDescription')}
        actions={(
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <InputGroup className="app-search">
              <InputGroup.Text>
                <IconSearch size={16} />
              </InputGroup.Text>
              <Form.Control
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder={t('invoices.searchPlaceholder')}
              />
            </InputGroup>
            <Form.Select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} style={{ minWidth: '180px' }}>
              <option value="all">{t('invoices.filters.all')}</option>
              <option value="open">{t('invoices.filters.open')}</option>
              <option value="paid">{t('invoices.filters.paid')}</option>
              <option value="overdue">{t('invoices.filters.overdue')}</option>
            </Form.Select>
          </div>
        )}
        bodyClassName={filteredInvoices.length > 0 ? 'p-0' : undefined}
      >
        {filteredInvoices.length === 0 ? (
          <EmptyState
            title={invoices.length === 0 ? t('invoices.emptyTitle') : t('invoices.noResultsTitle')}
            message={invoices.length === 0 ? t('invoices.emptyMessage') : t('invoices.noResultsMessage')}
            action={(searchTerm || statusFilter !== 'all') ? (
              <Button variant="outline-secondary" onClick={resetFilters}>
                {t('invoices.resetFilters')}
              </Button>
            ) : null}
          />
        ) : (
          <div className="table-responsive">
            <Table hover className="app-table mb-0">
              <thead>
                <tr>
                  <th>{t('invoices.table.name')}</th>
                  <th>{t('invoices.table.contract')}</th>
                  <th>{t('invoices.table.issued')}</th>
                  <th>{t('invoices.table.due')}</th>
                  <th>{t('invoices.table.version')}</th>
                  <th>{t('invoices.table.updatedBy')}</th>
                  <th>{t('invoices.table.status')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map((invoice) => (
                  <tr key={invoice.id}>
                    <td className="fw-semibold">{invoice.name || '-'}</td>
                    <td>{invoice.contract_name || '-'}</td>
                    <td>{formatDate(invoice.date_issued)}</td>
                    <td>{formatDate(invoice.date_due)}</td>
                    <td>{invoice.version || 0}</td>
                    <td>{invoice.changed_by?.username || t('common.notSet')}</td>
                    <td>
                      <StatusBadge
                        color={STATUS_COLORS[invoice.status] || '#6c7a91'}
                        label={invoice.status_display || invoice.status || '-'}
                      />
                    </td>
                    <td>
                      <div className="d-flex flex-wrap gap-2">
                        {canViewContracts && invoice.contract ? (
                          <Button as={Link} to={`/contracts/contract/${invoice.contract}`} size="sm" variant="outline-primary">
                            {t('invoices.actions.viewContract')}
                          </Button>
                        ) : null}
                        {canChangeInvoices && invoice.status === 'open' ? (
                          <Button
                            size="sm"
                            variant="outline-success"
                            onClick={() => markAsPaidMutation.mutate(invoice.id)}
                            disabled={markAsPaidMutation.isPending}
                          >
                            <IconCheck size={14} className="me-1" />
                            {t('invoices.actions.markPaid')}
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </PageSection>
    </PageShell>
  );
};

export default Invoices;
